import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  useColumnSuggestionsQuery,
  useColumnSuggestionsV2Query,
  useTraitSuggestionsQuery,
} from "src/graphql";
import { useFlags } from "launchdarkly-react-client-sdk";

/**
 * One distinct value of a column (or trait) with how many rows carry it, as
 * top-K returns it. `count` is absent when the read format omits it or the
 * `columnSuggestionsHideCounts` flag withholds it.
 */
export type ColumnSuggestion = {
  value: string | number | boolean | null;
  count: number | undefined;
};

export function useFetchSuggestions(
  args: {
    // Optional so a trait caller can pass `traitId` alone: the body already
    // treats every field as absent-able, and the two lookups are exclusive.
    modelId?: string | undefined;
    columnName?: string | undefined;
    traitId?: string | undefined;
    query?: string | undefined;
  },
  options: { enabled?: boolean } = { enabled: true },
): {
  suggestions: ColumnSuggestion[];
  loadingSuggestions: boolean;
  onSearchUpdate: (search: string) => void;
} {
  const { enabled } = options;
  const isValidColumn = !!args.modelId && !!args.columnName;
  const isValidTrait = !!args.traitId;
  const [search, setSearch] = useState<string>("");
  const { enableNewColumnSuggestionsReads, columnSuggestionsHideCounts } =
    useFlags();
  const [debouncedSearch, { isPending: isSearchPending }] = useDebounce(
    search,
    // Debounce time in milliseconds.
    500,
  );

  const { data: newColumnSuggestionsData, isLoading: newLoadingSuggestions } =
    useColumnSuggestionsV2Query(
      {
        modelId: args.modelId?.toString() ?? "",
        columnName: args.columnName ?? "",
        // Empty string is a valid query grabbing the top values with no
        // filters applied.
        query: debouncedSearch ?? "",
      },
      {
        enabled:
          enableNewColumnSuggestionsReads &&
          enabled &&
          isValidColumn &&
          !isValidTrait,
      },
    );

  const { data: oldColumnSuggestionsData, isLoading: oldLoadingSuggestions } =
    useColumnSuggestionsQuery(
      {
        modelIds: args.modelId ? [args.modelId.toString()] : [],
        columnNames: args.columnName ? [args.columnName?.toString()] : [],
      },
      {
        enabled:
          !enableNewColumnSuggestionsReads &&
          enabled &&
          isValidColumn &&
          !isValidTrait,
      },
    );

  const { data: traitSuggestionsData, isLoading: traitSuggestionsLoading } =
    useTraitSuggestionsQuery(
      {
        traitId: args.traitId?.toString() ?? "",
        query: debouncedSearch ?? "",
      },
      {
        enabled: enabled && isValidTrait,
      },
    );

  const loadingSuggestions = isValidTrait
    ? traitSuggestionsLoading
    : enableNewColumnSuggestionsReads
      ? newLoadingSuggestions
      : oldLoadingSuggestions;

  const suggestions = isValidTrait
    ? traitSuggestionsData?.topKForTrait?.values
    : enableNewColumnSuggestionsReads
      ? newColumnSuggestionsData?.topKForQuery?.values
      : oldColumnSuggestionsData?.getTopK?.columns?.find(
          (column) =>
            column.modelId === args.modelId && column.name === args.columnName,
        )?.values;

  // Memoized so the array reference is stable across renders. Otherwise
  // every render of a PropertyFilter rebuilt this array, then cascaded into
  // suggestionOptions → multiSelectOptions, then into the Combobox /
  // BadgeInput re-rendering their full option list.
  const memoSuggestions = useMemo(
    () =>
      suggestions?.map((s) => ({
        ...s,
        count: columnSuggestionsHideCounts ? undefined : s.count,
      })) ?? [],
    [suggestions, columnSuggestionsHideCounts],
  );

  return {
    suggestions: memoSuggestions,
    loadingSuggestions: loadingSuggestions || isSearchPending(),
    onSearchUpdate: setSearch,
  };
}
